/* A5 — Saúde (Painel Administrativo).
 *
 * Visão cruzada de todos os clientes, ligada ao endpoint real `/api/health`:
 * processos internos, fila por plataforma e estado das conexões. Desenhada na
 * linguagem do Nebula (a A5 não foi desenhada no Claude Design; segue o sistema
 * e a A1 como referência).
 *
 * Consumo por número (volume de mensagens) é Fase 0 — marcado como nota. */

import React from 'react';

import { api } from '../api/cliente';
import { useRecurso } from '../estado/recurso';
import { duracao, relativo } from './formato';
import { Aviso, card, Cabecalho, Icon, Nota, Secao, Tag } from './ui';

function tomDoProcesso(silencio) {
  if (silencio == null) return 'neutral';
  if (silencio < 90) return 'success';
  if (silencio < 600) return 'warning';
  return 'danger';
}

function estadoDaConexao(estado) {
  if (estado === 'open') return { tone: 'success', rotulo: 'Conectado' };
  if (estado === 'connecting') return { tone: 'warning', rotulo: 'Conectando' };
  return { tone: 'danger', rotulo: 'Desconectado' };
}

export default function Saude() {
  const recurso = useRecurso((sinal) => api.saude(sinal), []);
  const dados = recurso.dados;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho titulo="Saúde" descricao="Processos, fila e conexões de todos os clientes, num só lugar." />

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 8px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {recurso.erro ? (
          <Secao><Aviso titulo="A saúde não carregou" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} /></Secao>
        ) : recurso.carregando && !dados ? (
          <Secao><Aviso titulo="Carregando…" /></Secao>
        ) : (
          <>
            <Secao titulo="Processos">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 1, background: 'var(--border-subtle)' }}>
                {(dados.processos || []).map((p) => {
                  const tom = tomDoProcesso(p.silencio_segundos);
                  return (
                    <div key={p.papel} style={{ background: 'var(--surface-card)', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ font: 'var(--type-body-strong)', textTransform: 'capitalize' }}>{p.papel}</span>
                        <Tag tone={tom} dot>{tom === 'success' ? 'Ativo' : tom === 'warning' ? 'Lento' : 'Silencioso'}</Tag>
                      </div>
                      <div className="nb-tab" style={{ display: 'flex', gap: 16, font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
                        <span>silêncio {duracao(p.silencio_segundos)}</span>
                        <span>{(p.ciclos ?? 0).toLocaleString('pt-BR')} ciclos</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Secao>

            <Secao titulo="Fila">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 1, background: 'var(--border-subtle)' }}>
                {[
                  ['Pendentes', dados.fila?.mensagens_pendentes],
                  ['Espera mais antiga', duracao(dados.fila?.espera_mais_antiga_segundos)],
                  ['Reservadas', dados.fila?.mensagens_reservadas],
                  ['Runs abertas', dados.fila?.runs_abertas],
                  ['Encaminhamentos', dados.fila?.encaminhamentos_pendentes],
                ].map(([rotulo, valor]) => (
                  <div key={rotulo} style={{ background: 'var(--surface-card)', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="nb-tab" style={{ font: 'var(--fw-bold) 28px/1.1 var(--font-display)', color: 'var(--text-primary)' }}>{valor ?? '—'}</span>
                    <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--ls-caps)' }}>{rotulo}</span>
                  </div>
                ))}
              </div>
            </Secao>

            <Secao titulo="Conexões">
              <div>
                {(dados.conexoes || []).map((c) => {
                  const est = estadoDaConexao(c.estado);
                  const icone = c.tipo === 'banco' ? 'database' : c.tipo === 'ia' ? 'cpu' : 'smartphone';
                  return (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ color: 'var(--text-muted)' }}><Icon name={icone} size={16} /></span>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                        <span style={{ font: 'var(--type-body-strong)', fontSize: 'var(--fs-body-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</span>
                        <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{c.detalhe}{c.verificado_em ? ` · ${relativo(c.verificado_em)}` : ''}</span>
                      </div>
                      <Tag tone={est.tone} dot>{est.rotulo}</Tag>
                    </div>
                  );
                })}
              </div>
            </Secao>

            <div style={{ ...card, padding: 20 }}>
              <span style={{ font: 'var(--type-caption)', color: 'var(--text-placeholder)' }}>
                Consumo por número (volume enviado/recebido, tendência) chega na Fase 0, junto da entidade de números.
              </span>
            </div>
            <Nota>Fonte: /api/health. Estado das instâncias é o espelho em cache do connectionState da Evolution.</Nota>
          </>
        )}
      </div>
    </div>
  );
}
